<template>
  <validation-observer ref="formValidator">
    <div class="my-5">
      <div class="mb-5">
        <section-title :has-line="true" title="Información del Contrato" />
        <div class="row ml-2">
          <div v-if="model.id" class="col-md-2">
            <div class="color-secondary-6 font-14 font-weight-normal">
              Año Fiscal:
            </div>
            <div class="color-black font-16 font-weight-normal pt-1">
              {{ fiscalYearDescription || currentFiscalYearDescription || '-' }}
            </div>
          </div>
          <div class="col-md-2">
            <validation-provider
              v-if="!IsReadOnly || canEditVigency"
              v-slot="{ errors }"
              rules="required"
            >
              <custom-date-picker
                v-model="model.effectiveFromDate"
                class="custom-date-picker__extended"
                :custom-error-msg="errorMsgs.startDate"
                :disabled="IsReadOnly && !canEditVigency"
                :error="errors[0]"
                label="Fecha de comienzo:"
                :min-date="canEditVigency ? null : minFromDate"
                placeholder="DD/MM/YYYY"
                @input="dateHandler"
              />
            </validation-provider>
            <div v-else>
              <div class="color-secondary-6 font-14 font-weight-normal">
                Fecha de comienzo:
              </div>
              <div class="color-black font-16 font-weight-normal pt-1">
                {{ model.effectiveFromDate | date }}
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="row">
              <div class="col-6">
                <validation-provider
                  v-if="!IsReadOnly || canEditVigency"
                  v-slot="{ errors }"
                  rules="required"
                >
                  <custom-date-picker
                    v-model="model.effectiveToDate"
                    class="custom-date-picker__extended"
                    :custom-error-msg="errorMsgs.endDate"
                    :disabled="IsReadOnly && !canEditVigency"
                    :error="errors[0]"
                    label="Fecha de finalización:"
                    :max-date="canEditVigency ? null : maxDate"
                    :min-date="canEditVigency ? null : minToDate"
                    placeholder="DD/MM/YYYY"
                    @input="dateHandler"
                  />
                </validation-provider>
                <div v-else>
                  <div class="color-secondary-6 font-14 font-weight-normal">
                    Fecha de finalización:
                  </div>
                  <div class="color-black font-16 font-weight-normal pt-1">
                    {{ model.effectiveToDate | date }}
                  </div>
                </div>
              </div>
              <div class="col">
                <div v-if="IsReadOnly && canEditVigency" class="col">
                  <div
                    style="position: relative;
                        padding-top: 2em;
                        right: 23px;"
                  >
                    <action-button
                      i="fas fa-save"
                      variant="outline-success"
                      @click="updateContractVigency"
                    >
                      Guardar
                    </action-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md">
            <div class="row">
              <div class="col-4">
                <validation-provider
                  v-if="!IsReadOnly || (IsReadOnly && canEditProposalNumber)"
                  v-slot="{ errors }"
                  rules="required"
                >
                  <custom-input
                    id="contractNumber"
                    v-model="model.contractNumber"
                    :custom-error-msg="errorMsgs.contract"
                    :error="errors[0]"
                    label="Número de Contrato:"
                    label-class="data-name"
                    name="contractNumber"
                  />
                </validation-provider>
                <div v-else>
                  <div class="color-secondary-6 font-14 font-weight-normal">
                    Número de Contrato:
                  </div>
                  <div class="color-black font-16 font-weight-normal pt-1">
                    {{ model.contractNumber | empty }}
                  </div>
                </div>
              </div>
              <div
                v-if="!IsReadOnly || (IsReadOnly && model.isDirectPayment)"
                class="col-4"
              >
                <div>
                  <span class="data-label">Contrato específico para:</span>
                  <div class="row py-2">
                    <div class="col">
                      <check-box
                        v-model="model.isDirectPayment"
                        :disabled="IsReadOnly"
                        text="Pago Directo"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="IsReadOnly && canEditProposalNumber" class="col">
                <div
                  style="position: relative;
                         padding-top: 2em;
                         right: 23px;"
                >
                  <action-button
                    i="fas fa-save"
                    variant="outline-success"
                    @click="updateContractNumber"
                  >
                    Guardar
                  </action-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showAmendmentRecomendation">
        <div
          class="alert alert-danger-custom d-flex justify-content-between"
          role="alert"
          style="font-weight: bold;"
        >
          <div class="d-flex align-items-center">
            <span class="mr-3">
              <i class="fas fa-times-circle" />
            </span>
            <span>
              Debe seleccionar fechas fuera de la vigencia actual o enmendar el
              contrato en cuestión. El período de vigencia del mismo es:
              <br />
              Fecha de comienzo:
              <span style="font-weight: normal;">
                {{ conflictContract.dateFrom | date }};
              </span>
              Fecha de finalización:
              <span style="font-weight: normal;">
                {{ conflictContract.dateTo | date }}
              </span>
            </span>
          </div>
          <div class="d-flex align-items-center">
            <span
              v-if="
                conflictContract.isSigned && !conflictContract.isDirectPayment
              "
              class="redirect__action"
              @click="onAmendmentRedirect"
            >
              <u>Enmendar Contrato</u>
            </span>
            <span
              v-else-if="!conflictContract.isDirectPayment"
              class="redirect__action"
              @click="onEditRedirect"
            >
              <u>Modificar Contrato</u>
            </span>
          </div>
        </div>
      </div>

      <div v-if="showInformationSection">
        <div v-if="hasViewDocumentPermission" class="mb-5">
          <section-title :has-line="true" title="Documento de Contrato" />
          <documents
            v-model="model.documents"
            :auto-upload="!!model.id"
            :available-document-types="availableDocumentTypes"
            :can-remove="!model.isSignedFlag || canRemoveDocument"
            :disabled="!hasViewDocumentPermission || readOnly"
            :on-download="onDownload"
            @onRemove="onRemoveDocuments"
            @onUpload="uploadDocument"
          />
        </div>

        <div v-if="showSeparationFunds" class="mb-5 ">
          <separation-of-funds
            ref="separationOfFunds"
            :contract-id="Number(model.id)"
            :fiscal-year-id="currentFiscalYearId || data.fiscalYearId"
            :is-contract-signed="model.isSignedFlag"
            own-save
            :read-only="true"
          />
        </div>

        <SIFDEInformation
          :academic-year="fiscalYearDescription"
          :company-id-value="companyIdValue"
          :contract-number="originalContract"
        />

        <service-information-list
          v-if="model.id"
          :add-enabled="
            !IsReadOnly || userHasPermissions('provider.contract.services.add')
          "
          :company-id="companyIdValue"
          :contract-id="model.id"
          :delete-enabled="
            !IsReadOnly &&
              userHasPermissions('provider.contract.services.delete')
          "
          :disabled="IsReadOnly"
          :edit-enabled="canEditService"
          :on-deleting="onServiceDeleting"
          :on-saving="onServiceSaving"
          :value="model.services"
        />

        <fixed-bottom-save-button
          v-if="showBtnSave && !IsReadOnly"
          :offset="150"
          @click="save"
        />
      </div>
    </div>
  </validation-observer>
</template>

<script>
import SectionTitle from '@/components/common/page/SectionTitle';
import CustomDatePicker from 'custom-date-picker';
import FixedBottomSaveButton from '@/components/common/FixedBottomSaveButton';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import alert from '@/mixins/alert.mixin';
import moment from 'moment';
import ServiceInformationList from './components/ServiceInformationList';
import storeMixin from '@/shared/mixins/storeGenerator';
import Documents from './components/Documents';
import service from '@/modules/app/api';
import { serialize as objectToFormData } from 'object-to-formdata';
import CustomInput from 'CustomInput';
import rejectArray from 'lodash/reject';
import SIFDEInformation from './components/SIFDE/SIFDEInformation';

import SeparationOfFunds from '@/views/provider/proposalHistory/tabs/SeparationOfFundsTabV2.vue';
import {
  getContractServices as _getContractServices,
  createContractService as _createContractService,
  editContractService as _editContractService,
  deleteContractService as _deleteContractService,
  insertContract as _insertContract,
  updateContract as _updateContract,
  getContract as _getContract,
  signContract as _signContract,
  getContractDocumentTypes,
  uploadContractDocument,
  removeContractDocument,
  getSifdeContractInfo as _getSifdeContractInfo,
  getContractDocuments as _getContractDocuments,
  updateContractNumber as _updateContractNumber,
  updateContractVigency as _updateContractVigency,
  verifyConflict,
  getMinVigency,
} from '@/modules/provider/api.js';

export default {
  name: 'Edit',
  components: {
    SectionTitle,
    CustomDatePicker,
    ValidationProvider,
    ValidationObserver,
    ServiceInformationList,
    FixedBottomSaveButton,
    Documents,
    CustomInput,
    SeparationOfFunds,
    SIFDEInformation,
  },
  mixins: [alert, storeMixin],
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    companyId: {
      type: Number,
      default: null,
    },
    contractIdValue: {
      type: Number,
      default: null,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      //TODO: Traer mensajes de backend
      errorMsgs: {
        startDate: 'Fecha de comienzo es requerida.',
        endDate: 'Fecha de finalización es requerida.',
        contract: 'Número de contrato es requerido.',
        list: 'Información de monto es requerido.',
        informationRequired: 'Información Requerida',
      },
      accountNumbers: [],
      accountNumbersSelected: [],
      years: [],
      fiscalYearDescription: null,
      currentFiscalYearId: null,
      currentFiscalYearDescription: null,
      diciplines: [],
      minFromDate: null,
      minToDate: null,
      maxDate: null,
      availableDocumentTypes: [],
      model: {
        id: null,
        companyId: null,
        fiscalYearId: parseInt(this.$router.currentRoute.params.fiscalYearId),
        fiscalYearDescription: this.fiscalYearDescription,
        contractNumber: null,
        effectiveOriginalFromDate: null,
        effectiveFromDate: null,
        effectiveToDate: null,
        balanceMaxAmt: null,
        balancePendingAmt: null,
        documents: [],
        services: [],
        purchaseOrderNumbers: [],
        isSignedFlag: null,
        amendmentFlag: null,
        isDirectPayment: false,
      },
      conflictContract: {
        id: null,
        fiscalYearId: null,
        dateFrom: null,
        dateTo: null,
        isSigned: false,
      },
    };
  },
  computed: {
    canRemoveDocument() {
      return this.userHasPermissions('provider.contract.document.delete');
    },
    showInformationSection() {
      return (
        !!this.model.effectiveFromDate &&
        !!this.model.effectiveToDate &&
        (!this.conflictContract.id ||
          this.model.id == this.conflictContract.id) &&
        !this.model.isDirectPayment
      );
    },
    showAmendmentRecomendation() {
      return (
        (!!this.model.effectiveFromDate || !!this.model.effectiveToDate) &&
        !!this.conflictContract.id &&
        this.model.id != this.conflictContract.id
      );
    },
    accountNumbersUnSelected() {
      const accountNumbersSelected = this.accountNumbersSelected.map(
        (x) => x.purchaseOrderNumberId
      );
      return rejectArray(this.accountNumbers, (x) =>
        accountNumbersSelected.includes(x.purchaseOrderNumberId)
      );
    },
    originalContract() {
      return this.model.amendmentFlag == '1'
        ? this.model.parentContractNumber
        : this.model.contractNumber;
    },
    contractId() {
      return parseInt(this.$route.params.contractId) || this.contractIdValue;
    },
    canEditProposalNumber() {
      return this.userHasPermissions(
        'provider.contract.proposalrequest.editproposalnumber'
      );
    },
    IsReadOnly() {
      return (
        this.model.isSignedFlag === true ||
        (this.model.fiscalYearId > 0 &&
          this.currentFiscalYearId > this.model.fiscalYearId) ||
        this.readOnly
      );
    },
    showBtnSave() {
      return (
        !this.model.isSignedFlag &&
        this.model.fiscalYearId == this.currentFiscalYearId
      );
    },
    showSeparationFunds() {
      return this.userHasPermissions('provider.contract.fundsseparation');
    },
    showAmountInformation() {
      return this.userHasPermissions('provider.contract.showamount');
    },
    canEditService() {
      return (
        (!this.IsReadOnly &&
          this.userHasPermissions('provider.contract.services.edit')) ||
        (this.model.isSignedFlag &&
          this.userHasPermissions('provider.contract.services.edit.signed'))
      );
    },
    hasViewDocumentPermission() {
      return this.userHasPermissions('provider.contract.document.view');
    },
    companyIdValue() {
      return parseInt(this.$route.params.companyId) || this.companyId;
    },
    companyTypeName() {
      return this.data.companyTypeName;
    },
    fiscalYearDescriptionValue() {
      return (
        this.model.fiscalYearDescription || this.currentFiscalYearDescription
      );
    },
    canEditVigency() {
      return this.userHasPermissions('provider.contract.vigency.edit');
    },
  },
  watch: {
    model: {
      deep: true,
      handler() {
        this.$emit('on-change-model', this.model);
      },
      immediate: true,
    },
  },
  async mounted() {
    this.model.id = this.contractId;
    this.model.companyId = this.companyIdValue;

    await this.getAvailableDocumentTypes();
    await this.getContractServices();

    if (this.model.id) {
      await this.getContract();
      this.currentFiscalYearId = this.model.fiscalYearId;
      this.fiscalYearDescription = this.model.fiscalYearDescription;
      this.model.effectiveOriginalFromDate = this.model.effectiveFromDate;
      this.minFromDate = new Date(this.model.effectiveFromDate);
      this.minToDate = new Date(this.model.effectiveFromDate);
      this.maxDate = new Date(this.model.effectiveToDate);
    } else {
      await this.setDatesLimits();
    }
  },
  destroyed() {
    this.persistLazyData({ actions: [] });
  },
  methods: {
    async dateHandler(newValue) {
      this.minToDate = !newValue ? this.minFromDate : new Date(newValue);
      if (!newValue || !!this.IsReadOnly) return;

      const { data } = await verifyConflict(
        this.companyIdValue,
        this.model?.effectiveFromDate,
        this.model?.effectiveToDate
      );

      this.setConflictContract(data);
    },
    setConflictContract(data) {
      this.conflictContract = {
        id: data.conflictContractId,
        fiscalYearId: data.conflictContractFiscalYearId,
        dateFrom: data.conflictContractDateFrom,
        dateTo: data.conflictContractDateTo,
        isSigned: data.conflictContractIsSigned,
        isDirectPayment: data.conflictContractIsDirectPayment,
      };
    },
    async onEditRedirect() {
      await this.$router.push({
        path: `/provider/edit/0/${this.companyIdValue}/contract/${this.conflictContract.id}`,
      });
      window.location.reload();
    },
    onAmendmentRedirect() {
      this.$router.push({
        path: `/provider/amendment/${this.conflictContract.id}/${this.companyIdValue}/${this.conflictContract.fiscalYearId}`,
      });
    },
    async updateContractNumber() {
      if (this.contractId > 0) {
        await _updateContractNumber(this.contractId, this.model.contractNumber)
          .then(() => {
            this.ShowToast(
              'Proceso Exitoso',
              'El contrato fue guardado exitosamente',
              'success'
            );
          })
          .catch(() =>
            this.ShowToast(
              'Error',
              'Ocurrió un error cargando los datos',
              'error'
            )
          );
      }
    },
    async updateContractVigency() {
      const isValid = await this.$refs['formValidator'].validate();
      if (isValid && this.contractId > 0) {
        await _updateContractVigency(
          this.contractId,
          this.model.effectiveFromDate,
          this.model.effectiveToDate
        )
          .then(() => {
            this.ShowToast(
              'Proceso Exitoso',
              'La vigencia de contrato fue guardada exitosamente.',
              'success'
            );
          })
          .catch(() =>
            this.ShowToast(
              'Error',
              'Ocurrió un error al guardar los datos.',
              'error'
            )
          );
      }
    },
    addAccountNumber(accountNumber) {
      this.accountNumbersSelected.push(accountNumber);
      this.addingAccountNumbersMore = false;
    },
    async getContract() {
      if (this.model.id) {
        await _getContract(this.model.id)
          .then((resp) => {
            this.model = resp.data;
            this.FiscalYearId = resp.data.fiscalYearId;
            this.fiscalYearDescription = resp.data.fiscalYearDescription;
          })
          .catch(() =>
            this.ShowToast(
              'Error',
              'Ocurrió un error cargando los datos',
              'error'
            )
          );
      }
    },
    async save() {
      if (this.showAmendmentRecomendation) return;
      const isValid = await this.$refs['formValidator'].validate();

      if (isValid) {
        let from = moment(this.model.effectiveFromDate);
        let to = moment(this.model.effectiveToDate);
        if (from.isAfter(to)) {
          this.ShowToast(
            'Error',
            'La fecha incio de contrato es mayor que la fecha final',
            'error'
          );
          return;
        }
        this.model.balanceMaxAmt =
          this.model.balanceMaxAmt != null
            ? parseFloat(this.model.balanceMaxAmt)
            : 0;

        let modelCopy = Object.assign({}, this.model);

        //Clear the keyvalue models to reduce model size
        this.compressModel(modelCopy);

        let promise = null;
        modelCopy.isSignedFlag = modelCopy.isSignedFlag || false;
        //Contract for directpayment should not have services in it.
        modelCopy.services = !this.model.isDirectPayment
          ? modelCopy.services
          : [];

        if (!this.model.id) {
          promise = _insertContract(modelCopy).then((response) => {
            this.uploadDocuments(response.data);
            this.ShowToast(
              'Proceso Exitoso',
              'El contrato fue guardado exitosamente',
              'success'
            );

            this.model.id = response.data;
            this.model.isSignedFlag = false;
          });
        } else {
          promise = _updateContract(modelCopy).then(() => {
            this.uploadDocuments(this.model.id);
            this.ShowToast(
              'Proceso Exitoso',
              'El contrato fue guardado exitosamente',
              'success'
            );
          });
        }

        await promise.catch((error) => {
          this.ShowToast('Error', error.response.data.message, 'error');
        });

        await _getContract(this.model.id)
          .then((resp) => {
            this.model = resp.data;
            this.FiscalYearId = resp.data.fiscalYearId;
            this.fiscalYearDescription = resp.data.fiscalYearDescription;
          })
          .catch(() =>
            this.ShowToast(
              'Error',
              'Ocurrió un error cargando los datos',
              'error'
            )
          );
      } else {
        this.ShowToast(
          'Información Incompleta',
          'Favor de completar la información solicitada para poder continuar.',
          'error'
        );
      }
    },
    async uploadDocuments(contractId) {
      let documentsToAdd = this.model.documents.filter((d) => {
        return d.documentId == null || d.documentId == undefined;
      });

      if (!this.hasViewDocumentPermission || documentsToAdd.length == 0) {
        return;
      }

      const documents = documentsToAdd.map((d) => ({
        file: d.attachment,
        documentTypeId: d.documentType.id,
      }));

      await uploadContractDocument(
        contractId,
        objectToFormData(
          { documents },
          { indices: true, nullsAsUndefineds: true }
        )
      );
      await this.getContract();
    },
    compressModel(currentModel) {
      if (currentModel.services != null)
        currentModel.services.forEach((element) => {
          delete element.serviceType;
          delete element.disciplineType;
          delete element.serviceModalityType;
          delete element.locationType;
          delete element.therapyDuration;
        });
    },
    signContract() {
      this.ShowCancelModal(
        'Atención',
        `Certifico que la información documentada en el contrato del año fiscal <b>${this.fiscalYearDescriptionValue}</b> se encuentra debidamente documentada con todas sus partes, incluyendo el número y documento del contrato y su configuración de la tarifa. ¿Está seguro(a) que desea firmar el contrato del proveedor <b>${this.companyTypeName}</b> seleccionado?`,
        'Sí',
        'No'
      ).then((answer) => {
        if (answer) {
          _signContract(this.model.id)
            .then(() => {
              this.ShowToast(
                'Proceso Exitoso',
                'El contrato ha sido firmado exitosamente.',
                'success'
              );
              this.model.isSignedFlag = true;
            })
            .catch((error) => {
              this.ShowToast('Error', error.response.data.message, 'error');
            });
        }
      });
    },
    onPurchaseOrderNumberSaving(item) {
      item.lineNumber = +item.lineNumber;
      this.model.purchaseOrderNumbers.push({
        PurchaseOrderNumberId: item.PurchaseOrderNumberId,
        amount: item.amount,
        companyContractId: item.companyContractId,
        fullNumber: item.fullNumber,
        id: item.id,
        lineNumber: item.lineNumber,
        poNumber: item.poNumber,
        purchaseOrderNumberId: item.purchaseOrderNumberId,
        invoiced: item.invoiced,
      });

      this.model.balanceMaxAmt =
        this.model.balanceMaxAmt != null
          ? parseFloat(this.model.balanceMaxAmt)
          : 0;
      this.model.balanceMaxAmt += item.amount;
    },
    onPurchaseOrderNumberDeleting(index) {
      this.model.balanceMaxAmt =
        this.model.balanceMaxAmt != null
          ? parseFloat(this.model.balanceMaxAmt)
          : 0;

      if (
        this.model.balanceMaxAmt >=
        this.model.purchaseOrderNumbers[index].amount
      )
        this.model.balanceMaxAmt -= this.model.purchaseOrderNumbers[
          index
        ].amount;
      else this.model.balanceMaxAmt = 0;

      this.model.purchaseOrderNumbers.splice(index, 1);
    },
    async onServiceSaving(savedService) {
      // If there is no contract we dont do operation on the server
      if (this.model.id == null) return true;

      let promise = null;

      savedService.cost =
        savedService.cost != null ? parseFloat(savedService.cost) : null;

      if (savedService.id == null)
        promise = _createContractService(this.model.id, {
          disciplineTypeId: savedService.disciplineTypeId,
          locationTypeId: savedService.locationTypeId,
          schoolTypeId: savedService.schoolTypeId,
          serviceModalityTypeId: savedService.serviceModalityTypeId,
          serviceTypeId: savedService.serviceTypeId,
          therapyDurationId: savedService.therapyDurationId,
          cost: savedService.cost,
        });
      else
        promise = _editContractService({
          id: savedService.id,
          disciplineTypeId: savedService.disciplineTypeId,
          locationTypeId: savedService.locationTypeId,
          schoolTypeId: savedService.schoolTypeId,
          serviceModalityTypeId: savedService.serviceModalityTypeId,
          serviceTypeId: savedService.serviceTypeId,
          therapyDurationId: savedService.therapyDurationId,
          cost: savedService.cost,
        });

      let result = false;

      await promise
        .then((response) => {
          this.ShowToast(
            'Proceso Exitoso',
            'Servicio guardado exitosamente.',
            'success'
          );

          savedService.id = response.data.id;

          result = true;
        })
        .catch((error) => {
          this.ShowToast('Error', error.response.data.message, 'error');
          result = false;
        });

      return result;
    },
    async onServiceDeleting(serviceId) {
      // If there is no contract we dont do operation on the server
      if (this.model.id == null) return true;

      let promise = null;

      promise = _deleteContractService(serviceId);

      let result = false;

      await promise
        .then(() => {
          this.ShowToast(
            'Proceso Exitoso',
            'Servicio eliminado exitosamente.',
            'success'
          );
          result = true;
        })
        .catch((error) => {
          this.ShowToast('Error', error.response.data.message, 'error');
          result = false;
        });

      return result;
    },
    async getAvailableDocumentTypes() {
      let { data } = await getContractDocumentTypes();
      this.availableDocumentTypes = data;
    },
    async onDownload(documentId) {
      if (documentId != undefined) {
        service.downloadDocumentByAttachmentId(documentId);
      }
    },
    async onRemoveDocuments(documentId) {
      try {
        await removeContractDocument(documentId);
        this.ShowToast(
          'Proceso Exitoso',
          'Documento eliminado exitosamente',
          'success'
        );
      } catch (error) {
        this.ShowToast(
          'Error',
          'No ha sido posible eliminar el documento seleccionado.',
          'error'
        );
      }
      await this.getContractDocuments();
    },
    async uploadDocument(document) {
      try {
        const documents = [
          {
            file: document.attachment,
            documentTypeId: document.documentType.id,
          },
        ];
        await uploadContractDocument(
          this.contractId,
          objectToFormData(
            { documents },
            { indices: true, nullsAsUndefineds: true }
          )
        );

        this.ShowToast(
          'Proceso Exitoso',
          'El documento ha sido guardado exitosamente.',
          'success'
        );
        await this.getContractDocuments();
      } catch (error) {
        this.ShowToast(
          'Error',
          'No ha sido posible guardar el documento seleccionado.',
          'error'
        );
      }
    },
    async getContractDocuments() {
      let { data } = await _getContractDocuments(this.contractId);
      this.model.documents = [...data];
    },
    async getSifdeContractInfo() {
      this.model.contractNumber = (
        await _getSifdeContractInfo(this.companyIdValue)
      ).data?.contractId;
    },
    async getContractServices() {
      await _getContractServices(null, this.companyIdValue)
        .then((resp) => {
          this.model.services = resp.data;
        })
        .catch(() =>
          this.ShowToast(
            'Error',
            'Ocurrió un error cargando los datos',
            'error'
          )
        );
    },
    async setDatesLimits() {
      const { data } = await getMinVigency(this.companyIdValue);
      this.minFromDate = new Date(data.minDate);
      this.minToDate = new Date(data.minDate);
    },
  },
};
</script>

<style scoped lang="scss">
.custom-date-picker__extended ::v-deep {
  .custom-datetime-picker {
    position: absolute !important;
  }
}

.redirect__action {
  color: #5bb486;
  cursor: pointer;
}

.alert-danger-custom {
  color: #ec6662;
  background-color: #fcefef;
  border-color: #f4b5b3;
}
</style>
